<script setup>
/**
 * The people who need a call, by name.
 *
 * Everything above this on the page reports — how many, what is on today. This
 * is the part that hands over actual people, which is what turns "retention"
 * into work somebody can do this morning.
 *
 * The list is customers renewing soon. Each row shows when the person was
 * last active, so the reader can see who has gone quiet; the list itself is
 * not filtered on it. Each row carries the prototype's "At risk" label, and
 * "Why these?" states the one rule behind it — renewing soon.
 */
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { plural } from '../../../helpers/plural';
import { useBrokerScopeStore } from '../../../stores/brokerScope';
import XlAvatar from '../../xl/XlAvatar.vue';
import XlButton from '../../xl/XlButton.vue';
import XlCard from '../../xl/XlCard.vue';
import XlListRow from '../../xl/XlListRow.vue';
import XlPopover from '../../xl/XlPopover.vue';
import XlSkeleton from '../../xl/XlSkeleton.vue';
import XlTag from '../../xl/XlTag.vue';
import { relativeDay } from '../../../helpers/relativeDay';

const props = defineProps({
  watchlist: { type: Object, default: null },
  loading: { type: Boolean, default: false },
});

const router = useRouter();
const brokerScope = useBrokerScopeStore();

const days = computed(() => props.watchlist?.days ?? 60);
const people = computed(() => props.watchlist?.people ?? []);
const total = computed(() => props.watchlist?.total ?? 0);

// Only when there are more than the few named here — otherwise the link would
// promise a longer list than exists.
const hasMore = computed(() => total.value > people.value.length);

// Never a blank row beside a date. SSO may not have resolved the name, in which
// case the email identifies them well enough; if neither arrived, say plainly
// that somebody is there rather than rendering an empty line.
const nameOf = (person) => person.name || person.email || 'A policyholder';

const renewalLabel = (person) => {
  const days_ = person.days_remaining;

  if (days_ < 0) return `Overdue by ${Math.abs(days_)} ${Math.abs(days_) === 1 ? 'day' : 'days'}`;
  if (days_ === 0) return 'Renews today';
  if (days_ === 1) return 'Renews tomorrow';

  return `Renews in ${days_} days`;
};

const lastActive = (person) => {
  const label = relativeDay(person.last_seen_at);
  if (label === 'Today' || label === 'Yesterday') return label.toLowerCase();

  return label || 'never';
};

const secondLine = (person) =>
  [renewalLabel(person), `last active ${lastActive(person)}`, person.broker_name].filter(Boolean).join(' · ');

// "Why these?" explains the rule in place. The prototype sends it to an
// Insights page listing the at-risk rule; that page does not exist here, so
// the explanation comes to the link instead of the link going to it.
const whyOpen = ref(false);
const whyAnchor = ref(null);

/**
 * The list reads `?search=` on arrival, so this lands on exactly one person —
 * the same "the destination holds who the link said" property the count cards
 * keep. The broker filter is spread in first so the destination inherits the
 * scope the Overview was showing.
 */
const open = (person) => {
  if (!person.email) return;

  router.push({
    path: '/policyholders',
    query: { ...brokerScope.query, tab: 'registered', search: person.email },
  });
};

// Straight into that person's conversation: Messages opens a thread from
// `?thread=<local id>`, and a reply is the point of the row.
const message = (person) => {
  router.push({
    path: '/messages',
    query: { ...brokerScope.query, thread: String(person.id) },
  });
};

const seeAll = () => {
  router.push({
    path: '/policyholders',
    query: { ...brokerScope.query, tab: 'registered', renewal_within: String(days.value) },
  });
};
</script>

<template>
  <!-- Nobody due a call: no section at all. A quiet day is said once, by the
       greeting ("Nothing needs you yet"), not by an empty panel (Agency Hub
       Overview: "no empty panels"). -->
  <section v-if="props.loading || people.length" class="watchlist">
    <!-- The heading sits above the card, as the prototype's section headings
         do, with its explanation at the end of the line. -->
    <div class="watchlist__head">
      <h2 id="watchlist-title" class="watchlist__title xl-section-heading">Needs a person today</h2>
      <button
        ref="whyAnchor"
        type="button"
        class="watchlist__why"
        :aria-expanded="whyOpen ? 'true' : 'false'"
        data-testid="watchlist-why"
        @click="whyOpen = !whyOpen"
      >
        Why these?
      </button>
    </div>

    <XlPopover v-model:open="whyOpen" :anchor="whyAnchor" align="end" labelled-by="watchlist-why-title">
      <p id="watchlist-why-title" class="watchlist__why-title">Why these people</p>
      <!-- The horizon comes from the payload, so this and the "See all" link
           cannot drift apart. -->
      <p class="watchlist__why-body" data-testid="watchlist-why-body">
        Their policy renews within {{ plural(days, 'day') }}, soonest first. A renewal is
        the moment a customer is most likely to leave, so each is worth a
        personal message before it arrives.
      </p>
    </XlPopover>

    <XlCard padded>
      <XlSkeleton v-if="props.loading" :lines="3" height="44px" data-testid="watchlist-loading" />

      <div v-else>
        <div
          v-for="person in people"
          :key="person.sso_user_id"
          class="watchlist__row"
          :class="{ 'watchlist__row--link': person.email }"
          :role="person.email ? 'button' : undefined"
          :tabindex="person.email ? 0 : undefined"
          :data-testid="`watchlist-row-${person.sso_user_id}`"
          @click="open(person)"
          @keydown.enter="open(person)"
        >
          <XlListRow>
            <template #leading>
              <XlAvatar :name="nameOf(person)" :color="person.avatar_color || ''" :size="40" />
            </template>

            <template #title>{{ nameOf(person) }}</template>

            <template #subtitle>
              <span :data-testid="`watchlist-renewal-${person.sso_user_id}`">
                {{ secondLine(person) }}
              </span>
            </template>

            <template #trailing>
              <span class="watchlist__actions">
                <XlTag tone="warning" :data-testid="`watchlist-risk-${person.sso_user_id}`">At risk</XlTag>
                <!-- `.stop`: the row itself opens the person's record. -->
                <XlButton
                  v-if="person.id"
                  variant="ghost"
                  size="sm"
                  :data-testid="`watchlist-message-${person.sso_user_id}`"
                  @click.stop="message(person)"
                  @keydown.enter.stop
                >
                  Message
                </XlButton>
              </span>
            </template>
          </XlListRow>
        </div>

        <button
          v-if="hasMore"
          type="button"
          class="watchlist__more"
          data-testid="watchlist-see-all"
          @click="seeAll"
        >
          See all {{ total }}
        </button>
      </div>
    </XlCard>
  </section>
</template>

<style scoped>
/* Same rhythm as "Make something", the other section whose heading sits
   outside its card. */
.watchlist {
  margin-top: 20px;
}

.watchlist__head {
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 10px;
}

.watchlist__title {
  display: flex;
  flex: 1;
  align-items: baseline;
  gap: 12px;
}

.watchlist__title::after {
  content: '';
  flex: 1;
  height: 1px;
  background: var(--xl-color-border);
}

.watchlist__why {
  padding: 0;
  border: 0;
  background: none;
  color: var(--xl-color-primary);
  font: inherit;
  font-size: var(--xl-type-body-medium-size);
  font-weight: var(--xl-font-weight-bold);
  cursor: pointer;
}

.watchlist__why:focus-visible {
  outline: 2px solid var(--xl-color-primary);
  outline-offset: 2px;
}

.watchlist__why-title {
  margin: 0 0 6px;
  font-weight: var(--xl-font-weight-bold);
  color: var(--xl-color-on-surface);
}

.watchlist__why-body {
  margin: 0;
  font-size: var(--xl-type-body-medium-size);
  color: var(--xl-color-on-surface-variant);
}

.watchlist__actions {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.watchlist__row--link {
  cursor: pointer;
}

/* Bleeds into the card's padding and rounds off, rather than stopping flush
   against it — see ScheduleRow, which draws its hover the same way. */
.watchlist__row::before {
  content: '';
  position: absolute;
  inset: 1px -10px;
  border-radius: 10px;
  pointer-events: none;
}

/* The brokers table's tint, so every hovered row on this page is one colour. */
.watchlist__row--link:hover::before {
  background: var(--xl-color-primary-container);
}

.watchlist__row :deep(.xl-list-row) {
  position: relative;
}

.watchlist__row :deep(.xl-list-row__text b),
.watchlist__row :deep(.xl-list-row__text span) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.watchlist__row--link:focus-visible {
  outline: 2px solid var(--xl-color-primary);
  outline-offset: -2px;
}

.watchlist__empty {
  margin: 18px 0 6px;
  font-size: var(--xl-type-body-medium-size);
  color: var(--xl-color-outline);
}

.watchlist > :deep(.xl-card) {
  padding: 8px 22px;
}

.watchlist__row {
  position: relative;
  border-top: 1px solid var(--xl-color-border);
}

.watchlist__row:first-child {
  border-top: 0;
}

.watchlist__more {
  padding: 12px 0 2px;
  border: 0;
  background: none;
  color: var(--xl-color-primary);
  font: inherit;
  font-weight: var(--xl-font-weight-bold);
  cursor: pointer;
}

.watchlist__more:focus-visible {
  outline: 2px solid var(--xl-color-primary);
  outline-offset: -2px;
}
</style>
